import React from 'react';
import NextLink, { LinkProps as NextLinkProps } from 'next/link';

type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;

export const isExternalHref = (href: string) =>
  /^https?:\/\//.test(href) ||
  href.startsWith('mailto:') ||
  href.startsWith('tel:') ||
  href.startsWith('sms:') ||
  href.startsWith('//');

export type LinkRoutingInput = {
  href: string;
  external?: boolean;
  target?: React.HTMLAttributeAnchorTarget;
  rel?: string;
};

export type ResolvedLinkRouting = {
  external: boolean;
  target: React.HTMLAttributeAnchorTarget | undefined;
  rel: string | undefined;
};

export const resolveLinkRouting = ({
  href,
  external: externalProp,
  target,
  rel,
}: LinkRoutingInput): ResolvedLinkRouting => {
  const external = externalProp ?? isExternalHref(href);
  const computedTarget = target !== undefined ? target : external ? '_blank' : undefined;
  const needsRel = computedTarget === '_blank';
  const computedRel = needsRel ? (rel ?? 'noopener noreferrer') : rel;

  return {
    external,
    target: computedTarget,
    rel: computedRel,
  };
};

export type NextLinkComposedProps = Omit<AnchorProps, 'href'> &
  Pick<
    NextLinkProps,
    'href' | 'as' | 'replace' | 'scroll' | 'shallow' | 'prefetch' | 'locale'
  >;

export const NextLinkComposed = React.forwardRef<
  HTMLAnchorElement,
  NextLinkComposedProps
>(function NextLinkComposed(props, ref) {
  const { href, as, replace, scroll, shallow, prefetch, locale, ...other } = props;

  return (
    <NextLink
      href={href}
      as={as}
      replace={replace}
      scroll={scroll}
      shallow={shallow}
      prefetch={prefetch}
      locale={locale}
      ref={ref}
      {...other}
    />
  );
});

NextLinkComposed.displayName = 'NextLinkComposed';

export type NextLinkRoutingOptions = Pick<
  NextLinkProps,
  'as' | 'replace' | 'scroll' | 'shallow' | 'prefetch' | 'locale'
> & {
  href: string;
};

/** Builds a Next.js Link wrapper for MUI Link's `component` prop (avoids `as` prop conflict). */
export const createNextLinkWrapper = (options: NextLinkRoutingOptions) => {
  const { href, as, prefetch, replace, scroll, shallow, locale } = options;

  return React.forwardRef<HTMLAnchorElement, AnchorProps>(
    function NextLinkWrapper(wrapperProps, wrapperRef) {
      const { as: _muiAs, ...propsWithoutAs } = wrapperProps as AnchorProps & {
        as?: unknown;
      };

      const nextLinkProps: NextLinkComposedProps = {
        ...propsWithoutAs,
        href: wrapperProps.href?.toString() || href,
        ...(typeof as === 'string' && as ? { as } : {}),
        ...(prefetch !== undefined && { prefetch }),
        ...(replace !== undefined && { replace }),
        ...(scroll !== undefined && { scroll }),
        ...(shallow !== undefined && { shallow }),
        ...(locale !== undefined && { locale }),
      };

      return <NextLinkComposed ref={wrapperRef} {...nextLinkProps} />;
    },
  );
};

export const ExternalLinkIcon = () => (
  <svg
    aria-hidden
    focusable="false"
    width="0.9em"
    height="0.9em"
    viewBox="0 0 24 24"
    style={{ marginLeft: '0.35em', verticalAlign: 'text-top', opacity: 0.8 }}
  >
    <path
      fill="currentColor"
      d="M14 3h7v7h-2V6.41l-9.29 9.3l-1.42-1.42l9.3-9.29H14V3ZM5 5h7v2H7v10h10v-5h2v7H5V5Z"
    />
  </svg>
);
